/**
 * Recorded-birth-time prior — the entered clock treated as EVIDENCE, tiered by certainty.
 *
 * Why this exists (owner re-scope 2026-07-02, superseding the earlier "the entered time only
 * bounds the SEARCH WINDOW and never biases the ranking" rule for the recorded/approximate
 * tiers): a DOCUMENTED clock reading (birth certificate, hospital record) is the strongest
 * single piece of evidence a rectification ever has — classical practice (PLAN §2) tests a
 * reliable recorded time at ±minutes and uses events to REFINE it, not to overrule it.
 * Without a prior, a handful of date-only events can silently outvote the certificate: the
 * measured failure case is Rahul (recorded 06:17 = Aries rising; the ±2 h `recorded` window
 * spans Pisces/Aries/Taurus, and 3 events — 2 of them in one Jupiter-Moon period, plus a
 * Pisces stellium that any 11th-house rotation inherits — ranked Taurus first).
 *
 * Design language (same as the strength/Ashtakavarga gates): a BOUNDED multiplicative factor,
 * never a veto. 1.0 at the recorded clock, decaying toward a FLOOR with distance:
 *
 *   factor = floor + (1 − floor) · exp(−½ (Δ/scale)²)
 *
 *   • `recorded`    — floor 0.80, scale 45 min: a certificate is hard evidence, but a decisive
 *     events lead (>~25%) can still overturn a mis-copied clock.
 *   • `approximate` — floor 0.90, scale 180 min: family memory pulls gently.
 *   • `range` / `unknown` — factor 1.0 everywhere: a range is honestly agnostic inside itself
 *     (this preserves the original owner rule for the two range intake modes, and keeps every
 *     blind benchmark byte-identical — the accuracy-harness profiles carry no certainty).
 *
 * Sign-level use takes the factor at the sign window's CLOSEST APPROACH to the recorded clock
 * (Δ = 0 when the clock falls inside the window): the question at Level 0 is "could this sign
 * be rising at/near the recorded time", not "is its best-scoring minute near it".
 */

import { pymod } from "./pyutil";

export interface TimePriorTier {
  scale_minutes: number;
  floor: number;
}

export const TIME_PRIOR_TIERS: Record<string, TimePriorTier> = {
  recorded: { scale_minutes: 45.0, floor: 0.8 },
  approximate: { scale_minutes: 180.0, floor: 0.9 },
};

/** "HH:MM" / "HH:MM:SS" → seconds since midnight, or null when absent/unparseable. */
export function parse_time_seconds(value: unknown): number | null {
  if (typeof value !== "string" || !value.trim()) return null;
  const parts = value.trim().split(":").map((p) => parseInt(p, 10));
  if (parts.length < 2 || parts.some((n) => Number.isNaN(n))) return null;
  const [h, m, s = 0] = parts;
  if (h < 0 || h > 23 || m < 0 || m > 59 || s < 0 || s > 59) return null;
  return h * 3600 + m * 60 + s;
}

/** Shortest clock distance between two times-of-day, in seconds (midnight-wrap aware). */
function _clock_delta_seconds(a: number, b: number): number {
  const d = Math.abs(pymod(a, 86400) - pymod(b, 86400));
  return Math.min(d, 86400 - d);
}

function _factor(tier: TimePriorTier, delta_seconds: number): number {
  const d = delta_seconds / 60.0 / tier.scale_minutes;
  return tier.floor + (1.0 - tier.floor) * Math.exp(-0.5 * d * d);
}

/**
 * The prior factor (0.8..1.0) for one candidate moment. 1.0 whenever the certainty tier carries
 * no prior (range/unknown/absent) or no entered time is stored.
 */
export function time_prior_factor(
  certainty: unknown,
  entered_seconds: number | null,
  candidate_seconds: number,
): number {
  const tier = TIME_PRIOR_TIERS[String(certainty ?? "").trim().toLowerCase()];
  if (!tier || entered_seconds === null) return 1.0;
  return _factor(tier, _clock_delta_seconds(candidate_seconds, entered_seconds));
}

/**
 * The prior factor for a whole SIGN WINDOW [start, end] (seconds-of-day; end may wrap past
 * midnight, i.e. start > end): the factor at the window's closest approach to the entered time.
 */
export function time_prior_for_window(
  certainty: unknown,
  entered_seconds: number | null,
  window_start_seconds: number,
  window_end_seconds: number,
): number {
  const tier = TIME_PRIOR_TIERS[String(certainty ?? "").trim().toLowerCase()];
  if (!tier || entered_seconds === null) return 1.0;
  const t = pymod(entered_seconds, 86400);
  const start = pymod(window_start_seconds, 86400);
  const end = pymod(window_end_seconds, 86400);
  const inside = start <= end ? t >= start && t <= end : t >= start || t <= end; // wraps midnight
  if (inside) return 1.0;
  const delta = Math.min(_clock_delta_seconds(t, start), _clock_delta_seconds(t, end));
  return _factor(tier, delta);
}
